import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { AuthContext } from "../context/AuthContext";
import "../styles/UserProfile.css";

const API = "https://tj.marenergy.ro/api.php";

export default function UserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);

  const token = localStorage.getItem("token");
  const viewerRole = String(currentUser?.role ?? "");
  const viewerId = String(currentUser?.user_id ?? currentUser?.id ?? "");
  const targetId = String(id);

  const [profile, setProfile] = useState(null);
  const [completedWorks, setCompletedWorks] = useState(0);
  const [presence, setPresence] = useState(null);

  // logs
  const [logs, setLogs] = useState([]);
  const [logsError, setLogsError] = useState("");
  const [logsLoading, setLogsLoading] = useState(false);

  // pagination + filters
  const [page, setPage] = useState(1);
  const limit = 10;

  const [sort, setSort] = useState("date"); // date | category
  const [dir, setDir] = useState("desc"); // asc | desc
  const [category, setCategory] = useState(""); // '' | status | vizita | general | prosumator
  const [totalPages, setTotalPages] = useState(1);

  // profile loading
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // activity chart
  const [activityGranularity, setActivityGranularity] = useState("day"); // day|month
  const [activityStart, setActivityStart] = useState("");
  const [activityEnd, setActivityEnd] = useState("");
  const [activityLabels, setActivityLabels] = useState([]);
  const [activityCounts, setActivityCounts] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");
  const chartRef = useRef(null);

  const isOwnProfile = useMemo(() => targetId === viewerId, [targetId, viewerId]);

  const hasActivityData = useMemo(
    () => Array.isArray(activityCounts) && activityCounts.some((x) => Number(x) > 0),
    [activityCounts]
  );
  
  const showChart = !activityLoading && hasActivityData;

  // ✅ Logs visible:
  // - role 1/2: orice profil
  // - orice rol: doar profilul propriu
  const canSeeLogs = useMemo(() => {
    if (viewerRole === "1" || viewerRole === "2") return true;
    return isOwnProfile;
  }, [viewerRole, isOwnProfile]);

  // ✅ cerință: dacă role 3 și NU e profilul lui => nu afișăm deloc secțiunea logs
  const hideLogsSection = useMemo(() => viewerRole === "3" && !isOwnProfile, [viewerRole, isOwnProfile]);

  const toggleDir = () => setDir((d) => (d === "desc" ? "asc" : "desc"));

  // --- Helpers chart labels (day: YYYY-MM-DD -> DD.MM)
  const formatChartLabel = (label) => {
    if (!label) return "";
    if (activityGranularity === "month") return label; // YYYY-MM
    const m = String(label).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return label;
    return `${m[3]}.${m[2]}`;
  };

  const activityAvg = useMemo(() => {
    if (!activityCounts?.length) return 0;
    const sum = activityCounts.reduce((a, b) => a + (Number(b) || 0), 0);
    return sum / activityCounts.length;
  }, [activityCounts]);

  // 1) load profile
  useEffect(() => {
    const loadProfile = async () => {
      if (!token) {
        setErr("Nu ești autentificat (token lipsă).");
        setLoading(false);
        return;
      }

      setLoading(true);
      setErr("");

      try {
        const pRes = await axios.get(`${API}?action=get_user_profile&user_id=${targetId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setProfile(pRes.data?.user || null);
        setCompletedWorks(pRes.data?.completed_works || 0);
        setPresence(pRes.data?.presence || null);
      } catch (e) {
        setErr(e?.response?.data?.error || "Eroare la încărcare profil.");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [targetId, token]);

  // 2) load logs
  useEffect(() => {
    const loadLogs = async () => {
      if (!token) return;

      if (!canSeeLogs) {
        setLogs([]);
        setTotalPages(1);
        setLogsError("");
        return;
      }

      setLogsLoading(true);
      setLogsError("");

      try {
        const lRes = await axios.get(
          `${API}?action=get_user_logs&user_id=${targetId}&page=${page}&limit=${limit}` +
            `&sort=${encodeURIComponent(sort)}&dir=${encodeURIComponent(dir)}&category=${encodeURIComponent(category)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setLogs(lRes.data?.logs || []);
        setTotalPages(lRes.data?.total_pages || 1);
      } catch (e) {
        setLogsError(e?.response?.data?.error || "Eroare la încărcare loguri.");
      } finally {
        setLogsLoading(false);
      }
    };

    loadLogs();
  }, [targetId, token, canSeeLogs, page, sort, dir, category]);

  // 3) load activity chart (doar dacă profilul vizitat are role 3)
  useEffect(() => {
    const loadActivity = async () => {
      if (!token) return;
      if (!profile) return;

      setActivityLoading(true);
      setActivityError("");

      try {
        let start = activityStart;
        let end = activityEnd;

        if (!start || !end) {
          const today = new Date();
          const pad = (n) => String(n).padStart(2, "0");
          end = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

          if (activityGranularity === "day") {
            const d = new Date(today);
            d.setDate(d.getDate() - 30);
            start = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          } else {
            const d = new Date(today);
            d.setMonth(d.getMonth() - 11);
            start = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
          }

          setActivityStart(start);
          setActivityEnd(end);
        }

        const res = await axios.get(
          `${API}?action=get_user_status23_activity&user_id=${targetId}` +
            `&granularity=${encodeURIComponent(activityGranularity)}` +
            `&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setActivityLabels(res.data?.labels || []);
        setActivityCounts(res.data?.counts || []);
      } catch (e) {
        setActivityError(e?.response?.data?.error || "Eroare la încărcare grafic.");
      } finally {
        setActivityLoading(false);
      }
    };

    loadActivity();
  }, [token, targetId, profile, activityGranularity, activityStart, activityEnd]);

  // 4) scroll chart la cea mai recentă bară (dreapta)
  useEffect(() => {
    if (!chartRef.current) return;
    if (!activityLabels?.length) return;
    const t = setTimeout(() => {
      chartRef.current.scrollLeft = chartRef.current.scrollWidth;
    }, 50);
    return () => clearTimeout(t);
  }, [activityLabels, activityGranularity]);

  // Export Excel (toate, nu doar pagina)
  const handleDownloadLogsExcel = async (e) => {
    e.preventDefault();
    if (!token) return;

    try {
      const res = await axios.get(
        `${API}?action=get_user_logs&user_id=${targetId}&export=1` +
          `&sort=${encodeURIComponent(sort)}&dir=${encodeURIComponent(dir)}&category=${encodeURIComponent(category)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const all = Array.isArray(res.data?.logs) ? res.data.logs : [];
      if (all.length === 0) {
        alert("Nu există loguri pentru export.");
        return;
      }

      const rows = all.map((r, idx) => ({
        Nr: idx + 1,
        Data: r.created_at || "",
        Client: r.client_name || (r.client_id ? `Client #${r.client_id}` : "-"),
        Categorie: r.category || "",
        Mesaj: r.message || "",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      if (ws["!ref"]) ws["!autofilter"] = { ref: ws["!ref"] };
      ws["!cols"] = [{ wch: 5 }, { wch: 20 }, { wch: 30 }, { wch: 14 }, { wch: 80 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Loguri");

      const fileName = `log_user_${profile?.name || targetId}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      alert(err?.response?.data?.error || "Eroare la export Excel.");
    }
  };

  if (loading) {
    return (
      <div className="up-page">
        <div className="up-container">
          <div className="up-loading">
            <div className="spinner-border text-secondary mb-3" role="status" />
            <p>Se încarca profilul...</p>
          </div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="up-page">
        <div className="up-container">
          <button className="btn btn-outline-secondary mb-3" onClick={() => navigate(-1)} type="button">
            Inapoi
          </button>
          <div className="alert alert-danger">{err}</div>
        </div>
      </div>
    );
  }

  const isOnline = Boolean(presence?.is_online);
  const lastSeen = presence?.last_seen;

  const roleBadge = profile?.role ? `Rol: ${profile.role}` : null;
  const accessBadges = [
    profile?.i4a === "1" ? "i4a" : null,
    profile?.i4b === "1" ? "i4b" : null,
    profile?.i7 === "1" ? "i7" : null,
  ].filter(Boolean);
  

  return (
    <div className="up-page">
      <div className="up-container">
        {/* Header */}
        <div className="up-header">
          <div className="up-header-badges">
            <span className="up-user-name">👤 {currentUser?.name || "Utilizator"}</span>
          </div>
        </div>

        {/* Logo */}
        <div className="up-logo-wrap">
          <Link to="/dashboard">
            <img src="/images/Logo.webp" alt="Logo" className="up-logo" />
          </Link>
        </div>

        {/* Title row */}
        <div className="up-title-row">
          <button
            className="up-btn-primary"
            onClick={(e) => {
              e.preventDefault();
              navigate(-1);
            }}
            type="button"
          >
            Inapoi
          </button>

          <h1 className="up-title">Profil utilizator</h1>
          <div className="up-title-spacer" />
        </div>

        {/* Profile card */}
        <div className="up-card">
          <div className="up-card-top">
            <div className="up-profile-main">
              <h2 className="up-profile-name">{profile?.name || "User"}</h2>

              <div className="up-row">
                <span className="up-label">📞 Telefon:</span>
                <span className="up-value">{profile?.phone || "-"}</span>
              </div>

              {(viewerRole === "1" || viewerRole === "2") && (
                <div className="up-row">
                  <span className="up-label">✅ Lucrari finalizate:</span>
                  <span className="up-value">{completedWorks}</span>
                </div>
              )}
            </div>

            <div className="up-presence">
              <span className={`badge rounded-pill ${isOnline ? "bg-success" : "bg-secondary"}`}>
                {isOnline ? "Online" : "Offline"}
              </span>

              {!isOnline && lastSeen && <div className="up-lastseen">Ultima oara online: {lastSeen}</div>}

              <div className="up-presence-badges">
                {roleBadge && <span className="badge bg-dark rounded-pill">{roleBadge}</span>}
                {accessBadges.map((b) => (
                  <span key={b} className="badge bg-primary rounded-pill">
                    {b}
                  </span>
                ))}
                {accessBadges.length === 0 && <span className="up-muted">Niciun program</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Activity chart */}
        {showChart && (
          <div className="up-card">
            <div className="up-activity-head">
              <h2 className="up-section-title">Montaje finalizate</h2>

              <div className="up-activity-controls">
                <select
                  className="up-select"
                  value={activityGranularity}
                  onChange={(e) => {
                    setActivityGranularity(e.target.value);
                    setActivityStart("");
                    setActivityEnd("");
                  }}
                >
                  <option value="day">Pe zile</option>
                  <option value="month">Pe luni</option>
                </select>

                <input type="date" className="up-input" value={activityStart} onChange={(e) => setActivityStart(e.target.value)} />
                <input type="date" className="up-input" value={activityEnd} onChange={(e) => setActivityEnd(e.target.value)} />
              </div>
            </div>

            <div className="up-activity-meta">
              Medie: <strong>{activityAvg.toFixed(2)}</strong> / {activityGranularity === "day" ? "zi" : "lună"}
            </div>

            <div className="up-divider" />

            {activityLoading && <div className="up-muted">Se încarca graficul...</div>}
            {activityError && <div className="alert alert-danger">{activityError}</div>}

            {!activityLoading && !activityError && (
              <>
                {activityLabels.length === 0 ? (
                  <div className="up-muted">Nu exista schimbari 2 → 3 în intervalul selectat.</div>
                ) : (
                  <div className="up-activity-chart-wrap">
                    <div className="up-activity-chart" ref={chartRef}>
                      {(() => {
                        const max = Math.max(...activityCounts, 1);
                        return activityLabels.map((label, idx) => {
                          const val = activityCounts[idx] ?? 0;
                          const pct = (val / max) * 100;
                          return (
                            <div className="up-bar" key={`${label}-${idx}`} title={`${label}: ${val}`}>
                              <div className="up-bar-col">
                                <div className="up-bar-fill" style={{ height: `${pct}%` }} />
                              </div>
                              <div className="up-bar-label">{formatChartLabel(label)}</div>
                              <div className="up-bar-value">{val}</div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Logs card (scos complet pentru role 3 pe alt profil) */}
        {!hideLogsSection && (
          <div className="up-card">
            <div className="up-logs-head">
              <h2 className="up-section-title up-logs-title">Log user</h2>

              {canSeeLogs && (
                <div className="up-logs-filters">
                  <select
                    className="up-select"
                    value={sort}
                    onChange={(e) => {
                      setSort(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="date">Sortare: Data</option>
                    <option value="category">Sortare: Categorie</option>
                  </select>

                  <select
                    className="up-select"
                    value={dir}
                    onChange={(e) => {
                      setDir(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="desc">Descrescator</option>
                    <option value="asc">Crescator</option>
                  </select>

                  <select
                    className="up-select"
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">Filtru: Toate categoriile</option>
                    <option value="status">status</option>
                    <option value="vizita">vizita</option>
                    <option value="general">general</option>
                    <option value="prosumator">prosumator</option>
                  </select>
                </div>
              )}
            </div>

            {!canSeeLogs && (
              <div className="alert alert-warning" style={{ marginTop: 10 }}>
                Nu ai permisiuni să vezi logurile acestui user.
              </div>
            )}

            {canSeeLogs && logsError && (
              <div className="alert alert-danger" style={{ marginTop: 10 }}>
                {logsError}
              </div>
            )}

            {canSeeLogs && !logsError && (
              <>
                <div className="up-logs-gap" />

                <div className="up-table-wrap">
                  <table className="up-table">
                    <thead>
                      <tr>
                        <th
                          className="up-th-click"
                          title="Click pentru sortare după dată"
                          onClick={(e) => {
                            e.preventDefault();
                            setSort("date");
                            setPage(1);
                            toggleDir();
                          }}
                        >
                          Data {sort === "date" ? (dir === "desc" ? "↓" : "↑") : ""}
                        </th>
                        <th>Client</th>
                        <th
                          className="up-th-click"
                          title="Click pentru sortare după categorie"
                          onClick={(e) => {
                            e.preventDefault();
                            setSort("category");
                            setPage(1);
                            toggleDir();
                          }}
                        >
                          Categorie {sort === "category" ? (dir === "desc" ? "↓" : "↑") : ""}
                        </th>
                        <th>Mesaj</th>
                      </tr>
                    </thead>

                    <tbody>
                      {logsLoading ? (
                        <tr>
                          <td colSpan={4} className="up-muted-td">
                            Se încarca logurile...
                          </td>
                        </tr>
                      ) : logs.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="up-muted-td">
                            Niciun log gasit.
                          </td>
                        </tr>
                      ) : (
                        logs.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.created_at}</td>
                            <td>
                              {row.client_id ? (
                                <Link to={`/clients/${row.client_id}`} className="up-client-link">
                                  {row.client_name || `Client #${row.client_id}`}
                                </Link>
                              ) : (
                                <span className="up-muted">-</span>
                              )}
                            </td>
                            <td>{row.category || "-"}</td>
                            <td className="up-message-cell">{row.message}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="up-pagination">
                  <button
                    className="btn btn-outline-secondary"
                    disabled={page <= 1 || logsLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    type="button"
                  >
                    ← Inapoi
                  </button>

                  <div className="up-pageinfo">
                    Pagina <strong>{page}</strong> din <strong>{totalPages}</strong>
                  </div>

                  <button
                    className="btn btn-outline-secondary"
                    disabled={page >= totalPages || logsLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((p) => Math.min(totalPages, p + 1));
                    }}
                    type="button"
                  >
                    Inainte →
                  </button>
                </div>

                <div className="up-export">
                  <button
                    className="up-btn-primary"
                    type="button"
                    onClick={handleDownloadLogsExcel}
                    disabled={logsLoading}
                  >
                    Descarca Excel
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
